import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "wouter";
import { useQuery } from "@tanstack/react-query";
import {
  resolveBranch,
  resolveHandle,
  type ResolveBranchParams,
  type ResolveMasterResponse,
  type ResolveParams,
} from "@workspace/api-client-react";
import { Helmet } from "react-helmet-async";
import PublicProfile from "./PublicProfile";
import NotFound from "./not-found";
import { BranchActions, BranchListItem, OpeningHours, formatOpenNow } from "@/components/branches";

function PageLoading() {
  return (
    <main aria-busy="true" aria-label="Loading page" className="mx-auto min-h-screen max-w-xl space-y-5 p-6">
      <div className="h-24 w-24 animate-pulse rounded-full bg-muted" />
      <div className="h-8 w-2/3 animate-pulse rounded bg-muted" />
      <div className="h-48 animate-pulse rounded-2xl bg-muted" />
    </main>
  );
}

function PageError({ retry }: { retry: () => void }) {
  return (
    <main className="mx-auto max-w-xl space-y-4 p-6 text-foreground">
      <p role="alert">Couldn't load this page. Try again.</p>
      <button className="min-h-11 rounded-xl bg-primary px-5 text-primary-foreground" onClick={retry}>Try again</button>
    </main>
  );
}

function isNotFound(error: unknown) {
  return !!error && typeof error === "object" && "status" in error && error.status === 404;
}

function CommonQRFooter() {
  return <footer className="p-6 text-center text-sm text-muted-foreground"><Link href="/" className="inline-flex min-h-11 items-center">Create your free CommonQR profile</Link></footer>;
}

export function MasterPage({ data, handle }: { data: ResolveMasterResponse; handle: string }) {
  const [located, setLocated] = useState<ResolveMasterResponse | null>(null);
  const [locating, setLocating] = useState(false);
  const [locationMessage, setLocationMessage] = useState("");
  const requestId = useRef(0);
  useEffect(() => () => { requestId.current += 1; }, []);
  const current = located ?? data;
  const brandHandle = current.parent?.handle ?? current.handle;
  const branches = current.branches;
  const displayName = current.content.name;
  const useLocationOnce = () => {
    if (!navigator.geolocation) {
      setLocationMessage("Location isn't available. Showing the current branches.");
      return;
    }
    const id = ++requestId.current;
    setLocating(true);
    setLocationMessage("");
    navigator.geolocation.getCurrentPosition(async ({ coords }) => {
      if (requestId.current !== id) return;
      const params: ResolveParams = { lat: coords.latitude.toFixed(4), lng: coords.longitude.toFixed(4) };
      try {
        const result = await resolveHandle(handle, params);
        if (requestId.current !== id) return;
        if (result.type === "master") setLocated(result);
        else setLocationMessage("Couldn't find nearby branches. Showing the current branches.");
      } catch {
        if (requestId.current === id) setLocationMessage("Couldn't find nearby branches. Showing the current branches.");
      } finally {
        if (requestId.current === id) setLocating(false);
      }
    }, () => {
      if (requestId.current !== id) return;
      setLocating(false);
      setLocationMessage("Couldn't use your location. Showing the current branches.");
    }, { timeout: 10000, maximumAge: 0 });
  };
  return (
    <main className="min-h-screen bg-background text-foreground">
      {current.parent && <div className="mx-auto max-w-xl px-4 pt-4 text-sm">by <Link className="inline-flex min-h-11 items-center underline" href={`/${current.parent.handle}`}>{current.parent.displayName}</Link></div>}
      <PublicProfile
        resolvedProfile={current.content}
        handle={current.handle}
        recordVisit={false}
        whiteLabel={current.whiteLabel}
        pageTitle={current.whiteLabel ? displayName : `${displayName} | CommonQR`}
      />
      <section className="mx-auto max-w-xl space-y-4 px-4 py-6">
        {branches.mode !== "none" && (
          <>
            <h2 className="text-xl font-semibold">{branches.mode === "nearest" ? `Nearest to you${branches.approximate ? " (approximate)" : ""}` : "Popular branches"}</h2>
            <ul className="space-y-4">{branches.items.slice(0, 3).map((branch, i) => <BranchListItem key={branch.slug ?? i} branch={branch} brandHandle={brandHandle} />)}</ul>
          </>
        )}
        {current.totalActiveBranches > 3 && <p className="text-sm text-muted-foreground">{current.totalActiveBranches} branches</p>}
        {(branches.mode !== "nearest" || branches.approximate) && <button onClick={useLocationOnce} disabled={locating} className="min-h-11 rounded-xl border border-border px-4 disabled:opacity-60">{locating ? "Finding nearby branches…" : "Use my location"}</button>}
        {locationMessage && <p role="status" className="text-sm text-muted-foreground">{locationMessage}</p>}
      </section>
    </main>
  );
}

export default function PublicHandle() {
  const { handle = "" } = useParams<{ handle: string }>();
  const [, navigate] = useLocation();
  const query = useQuery({
    queryKey: ["public-handle", handle],
    queryFn: () => resolveHandle(handle),
    retry: false,
    refetchOnWindowFocus: false,
  });
  useEffect(() => {
    if (query.data?.canonicalHandle && query.data.canonicalHandle !== handle) {
      navigate(`/${query.data.canonicalHandle}${window.location.search}`, { replace: true });
    }
  }, [query.data?.canonicalHandle, handle, navigate]);
  if (query.isPending) return <PageLoading />;
  if (query.error) return isNotFound(query.error) ? <NotFound /> : <PageError retry={() => void query.refetch()} />;
  if (!query.data) return <NotFound />;
  const data = query.data;
  // Wait for a canonical replacement before mounting the Card, avoiding a duplicate visit.
  if (data.canonicalHandle !== handle) return <PageLoading />;
  return data.type === "profile"
    ? <PublicProfile key={data.canonicalHandle} resolvedProfile={data.profile} handle={data.canonicalHandle} />
    : <MasterPage key={handle} data={data} handle={handle} />;
}

function BranchPageContent({ handle, branchSlug }: { handle: string; branchSlug: string }) {
  const qrPending = useRef(new URLSearchParams(window.location.search).get("src") === "qr");
  const query = useQuery({
    queryKey: ["public-branch", handle, branchSlug],
    queryFn: async () => {
      const params: ResolveBranchParams | undefined = qrPending.current ? { src: "qr" } : undefined;
      qrPending.current = false;
      try {
        return await resolveBranch(handle, branchSlug, params);
      } finally {
        if (params) {
          const url = new URL(window.location.href);
          if (url.pathname.endsWith(`/${handle}/${branchSlug}`)) {
            url.searchParams.delete("src");
            window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
          }
        }
      }
    },
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
    gcTime: 0,
  });
  if (query.isPending) return <PageLoading />;
  if (query.error) return isNotFound(query.error) ? <NotFound /> : <PageError retry={() => void query.refetch()} />;
  const data = query.data;
  if (!data) return <NotFound />;
  const closed = "status" in data && data.status === "closed";
  const title = `${data.branch.name} – ${data.master.displayName}`;
  return (
    <main className="min-h-screen bg-background text-foreground">
      <Helmet><title>{title}</title></Helmet>
      <div className="mx-auto max-w-xl space-y-6 px-4 py-8">
        <Link href={`/${data.master.handle}`} className="flex min-h-11 items-center gap-3">
          {data.master.avatar && <img src={data.master.avatar} alt="" className="h-12 w-12 rounded-full object-cover" />}
          <span className="font-semibold">{data.master.displayName}</span>
        </Link>
        <h1 className="break-words text-2xl font-bold">{data.branch.name}{closed ? " is closed" : ""}</h1>
        {!("status" in data) && (
          <>
            <p className="text-muted-foreground">{[data.branch.address, data.branch.city].filter(Boolean).join(", ")}</p>
            {formatOpenNow(data.branch.openNow) && <p className="text-sm font-medium">{formatOpenNow(data.branch.openNow)}</p>}
            <BranchActions branch={data.branch} brandHandle={data.master.handle} includeEmail />
            <OpeningHours hours={data.branch.hours} timezone={data.branch.timezone} />
            {data.branch.links.length > 0 && <section className="space-y-3"><h2 className="text-xl font-semibold">Links</h2>{data.branch.links.map((link, i) => <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" className="flex min-h-11 items-center break-words rounded-xl border border-border bg-card px-4 py-2">{link.label}</a>)}</section>}
          </>
        )}
        {closed && <Link href={`/${data.master.handle}`} className="inline-flex min-h-11 items-center underline">Visit {data.master.displayName}</Link>}
        {data.nearby.length > 0 && <section className="space-y-4"><h2 className="text-xl font-semibold">Other branches nearby</h2><ul className="space-y-4">{data.nearby.slice(0, 3).map((branch, i) => <BranchListItem key={branch.slug ?? i} branch={branch} brandHandle={data.master.handle} />)}</ul></section>}
      </div>
      {!data.master.whiteLabel && <CommonQRFooter />}
    </main>
  );
}

export function BranchPage() {
  const { handle = "", branchSlug = "" } = useParams<{ handle: string; branchSlug: string }>();
  return <BranchPageContent key={`${handle}/${branchSlug}`} handle={handle} branchSlug={branchSlug} />;
}
